import { AlertTriangle, EmailBlock, Inbox, Mail } from '@saleshandy/icons';
import React from 'react';
import {
  TestConfigResponse,
  TestSummaryResponse,
} from '../../../../../../types';
import Badge from '../../../../../../../../shared/design-system/components/atoms/badge';
import { TestStatus } from '../../../../../../enums/inbox-radar';
import { useFirebaseAnimation } from '../utils/use-firebase-animation';

interface FirebaseData {
  id: string;
  summary?: {
    otherCount: number | null;
    inboxCount: number | null;
    spamCount: number | null;
    notDeliveredCount: number | null;
    placementCount: number;
    totalEmails: number;
  };
  testProgress?: string;
  [key: string]: any;
}

interface IProps {
  inboxPlacement: TestSummaryResponse;
  testConfigData?: TestConfigResponse;
  handleCheckLatestStatus?: () => void;
  firebaseData?: FirebaseData | null;
  isStatusDelayActive?: boolean;
  forShareableReport?: boolean;
}

const InboxPlacementScoreCard: React.FC<IProps> = ({
  inboxPlacement,
  testConfigData,
  firebaseData,
  isStatusDelayActive,
  forShareableReport,
}) => {
  const { inbox, spam, others, notDelivered } = inboxPlacement || {};

  // Use Firebase animation hook
  const { shouldAnimateItem } = useFirebaseAnimation(firebaseData, {
    duration: 2000,
    fields: ['inboxCount', 'spamCount', 'otherCount', 'notDeliveredCount'],
  });

  // Calculate placement data from Firebase if available
  const getFirebasePlacementData = () => {
    if (!firebaseData?.summary) {
      return null;
    }

    const {
      inboxCount,
      spamCount,
      otherCount,
      notDeliveredCount,
      placementCount,
    } = firebaseData.summary;

    // Handle null values by converting them to 0
    const safeInboxCount = inboxCount ?? 0;
    const safeSpamCount = spamCount ?? 0;
    const safeOtherCount = otherCount ?? 0;
    const safeNotDeliveredCount = notDeliveredCount ?? 0;

    // Special case: if placementCount is 0 but we have valid counts, use totalEmails
    const effectiveTotal = placementCount > 0 && placementCount;

    // Validate that effectiveTotal is valid
    if (effectiveTotal <= 0) {
      return null;
    }

    // Helper function to safely calculate percentage
    const calculatePercentage = (count: number, total: number): number => {
      if (total <= 0) return 0;
      const percentage = (count / total) * 100;
      return Math.round(percentage);
    };

    const calculatedData = {
      inbox: {
        percentage: calculatePercentage(safeInboxCount, effectiveTotal),
        value: safeInboxCount,
      },
      spam: {
        percentage: calculatePercentage(safeSpamCount, effectiveTotal),
        value: safeSpamCount,
      },
      others: {
        percentage: calculatePercentage(safeOtherCount, effectiveTotal),
        value: safeOtherCount,
      },
      notDelivered: {
        percentage: calculatePercentage(safeNotDeliveredCount, effectiveTotal),
        value: safeNotDeliveredCount,
      },
    };

    return calculatedData;
  };

  // Use Firebase data if available, otherwise fall back to API data
  const firebasePlacementData = getFirebasePlacementData();
  const placementData = firebasePlacementData || {
    inbox: inbox || { percentage: null, value: null },
    spam: spam || { percentage: null, value: null },
    others: others || { percentage: null, value: null },
    notDelivered: notDelivered || { percentage: null, value: null },
  };

  // Determine if we should show in progress state
  const shouldShowInProgress = () => {
    // If Firebase data is available, check if sum of counts is 0
    if (firebaseData?.summary) {
      const {
        inboxCount,
        spamCount,
        otherCount,
        notDeliveredCount,
      } = firebaseData.summary;

      // Calculate sum of all counts (handle null values)
      const totalCount =
        (inboxCount ?? 0) +
        (spamCount ?? 0) +
        (otherCount ?? 0) +
        (notDeliveredCount ?? 0);

      // Show "Finding" only if total count is 0, otherwise show results
      return (
        totalCount === 0 && firebaseData?.testProgress === TestStatus.Active
      );
    }

    // For normal API data, also check if sum of counts is 0
    const normalTotalCount =
      (inbox?.value ?? 0) +
      (spam?.value ?? 0) +
      (others?.value ?? 0) +
      (notDelivered?.value ?? 0);

    return (
      normalTotalCount === 0 && testConfigData?.testStatus === TestStatus.Active
    );
  };

  const getStatusConfig = (status?: string) => {
    const currentStatus =
      (isStatusDelayActive ? status : firebaseData?.testProgress) || status;

    switch (currentStatus) {
      case TestStatus.Active:
        return {
          text: 'In Progress',
          badgeClass: 'active',
        };
      case TestStatus.Paused:
        return {
          text: 'Paused',
          badgeClass: 'paused',
        };
      case TestStatus.Completed:
        return {
          text: 'Completed',
          badgeClass: 'completed',
        };
      case TestStatus.Stopped:
        return {
          text: 'Stopped',
          badgeClass: 'stopped',
        };
      default:
        return {
          text: '',
          badgeClass: '',
        };
    }
  };

  const statusConfig = getStatusConfig(testConfigData?.testStatus);

  return (
    <div className="test-report-summary-card-container-wrapper">
      <div className="test-report-summary-card-container">
        <div className="d-flex justify-content-between w-100">
          <div className="test-report-summary-card-header">
            <div className="d-flex align-items-center gap-12">
              <span className="test-report-summary-card-title">
                Inbox Placement Score
              </span>
              <div className={`inbox-score-chip ${statusConfig.badgeClass}`}>
                <Badge className="inbox-score-badge" />
                <span className="inbox-score-text">{statusConfig.text}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="test-report-summary-cards">
          <div className="test-report-summary-card inbox-placement">
            {shouldShowInProgress() && !forShareableReport ? (
              <span className="test-report-summary-card-finding">
                Finding <div className="loader" />
              </span>
            ) : (
              <span
                className={`test-report-summary-card-value ${
                  shouldAnimateItem('inboxCount')
                    ? 'esp-count-pop-animation inbox-placement'
                    : ''
                }`}
              >
                {placementData.inbox?.percentage != null
                  ? `${placementData.inbox.percentage}%`
                  : '-'}
              </span>
            )}
            <span className="test-report-summary-card-label">
              <Inbox /> Inbox
              <span
                className={`inbox-value ${
                  shouldAnimateItem('inboxCount')
                    ? 'esp-count-pop-animation inbox-placement'
                    : ''
                }`}
              >
                {placementData.inbox?.value}
              </span>
            </span>
          </div>
          <div className="test-report-summary-card spam-placement">
            {shouldShowInProgress() && !forShareableReport ? (
              <span className="test-report-summary-card-finding">
                Finding <div className="loader" />
              </span>
            ) : (
              <span
                className={`test-report-summary-card-value ${
                  shouldAnimateItem('spamCount')
                    ? 'esp-count-pop-animation spam-placement'
                    : ''
                }`}
              >
                {placementData.spam?.percentage != null
                  ? `${placementData.spam.percentage}%`
                  : '-'}
              </span>
            )}
            <span className="test-report-summary-card-label">
              <AlertTriangle /> Spam
              <span
                className={`inbox-value ${
                  shouldAnimateItem('spamCount')
                    ? 'esp-count-pop-animation spam-placement'
                    : ''
                }`}
              >
                {placementData.spam?.value}
              </span>
            </span>
          </div>
          <div className="test-report-summary-card other-placement">
            {shouldShowInProgress() && !forShareableReport ? (
              <span className="test-report-summary-card-finding">
                Finding <div className="loader" />
              </span>
            ) : (
              <span
                className={`test-report-summary-card-value ${
                  shouldAnimateItem('otherCount')
                    ? 'esp-count-pop-animation other-placement'
                    : ''
                }`}
              >
                {placementData.others?.percentage != null
                  ? `${placementData.others.percentage}%`
                  : '-'}
              </span>
            )}
            <span className="test-report-summary-card-label">
              <Mail /> Other
              <span
                className={`inbox-value ${
                  shouldAnimateItem('otherCount')
                    ? 'esp-count-pop-animation other-placement'
                    : ''
                }`}
              >
                {placementData.others?.value}
              </span>
            </span>
          </div>
          <div className="test-report-summary-card not-delivered-placement">
            {shouldShowInProgress() && !forShareableReport ? (
              <span className="test-report-summary-card-finding">
                Finding <div className="loader" />
              </span>
            ) : (
              <span
                className={`test-report-summary-card-value ${
                  shouldAnimateItem('notDeliveredCount')
                    ? 'esp-count-pop-animation not-delivered-placement'
                    : ''
                }`}
              >
                {placementData.notDelivered?.percentage != null
                  ? `${placementData.notDelivered.percentage}%`
                  : '-'}
              </span>
            )}
            <span className="test-report-summary-card-label">
              <EmailBlock /> Undelivered
              <span
                className={`inbox-value ${
                  shouldAnimateItem('notDeliveredCount')
                    ? 'esp-count-pop-animation not-delivered-placement'
                    : ''
                }`}
              >
                {placementData.notDelivered?.value}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InboxPlacementScoreCard;
